import { useCallback, useState } from "react";
import { type DynamicIslandPayload } from "../dynamicIsland.types";
import { type DynamicIslandAnimationPhase } from "./dynamicIslandAnimation.types";

interface UseDynamicIslandPresenceParams {

    /** Live orchestrator payload (undefined when closed). */
    "payload": DynamicIslandPayload | undefined;
}

interface UseDynamicIslandPresenceResult {

    /** Whether AnimatePresence should keep the shell mounted. */
    "isPresent": boolean;

    /** Current animation phase for occupancy callbacks. */
    "phase": DynamicIslandAnimationPhase;

    /** Called when a full close exit finishes (not on kind switch). */
    "handleExitComplete": () => void;

    /** Marks enter animation complete. */
    "handleEnterComplete": () => void;
}

/**
 * Tracks island open / close / exit phases for layout occupancy.
 * Exit animation is driven by removing the child from AnimatePresence
 * (do not keep a snapshot mounted — that prevents onExitComplete).
 *
 * @param params Hook parameters.
 * @returns Presence phase state for the shell and FAB row.
 */
export function useDynamicIslandPresence( params: UseDynamicIslandPresenceParams ): UseDynamicIslandPresenceResult {
    const { payload } = params;
    const [ hadPayload, setHadPayload ] = useState<boolean>( payload !== undefined );
    const [ isExiting, setIsExiting ] = useState<boolean>( false );
    const [ hasEntered, setHasEntered ] = useState<boolean>( false );

    if ( payload !== undefined ) {
        if ( !hadPayload ) {
            setHadPayload( true );
        }

        if ( isExiting ) {
            setIsExiting( false );
        }
    } else if ( hadPayload && !isExiting ) {
        setIsExiting( true );
    }

    const isPresent = payload !== undefined || isExiting;

    const phase: DynamicIslandAnimationPhase = !isPresent
        ? "idle"
        : payload !== undefined && !hasEntered
            ? "entering"
            : isExiting
                ? "exiting"
                : "visible";

    const handleExitComplete = useCallback( (): void => {
        setHadPayload( false );
        setIsExiting( false );
        setHasEntered( false );
    }, [] );

    const handleEnterComplete = useCallback( (): void => {
        setHasEntered( true );
    }, [] );

    return {
        isPresent,
        phase,
        handleExitComplete,
        handleEnterComplete,
    };
}
